import { Key, memo, useCallback, useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatDate, formatNome, formatSingularOrPlural } from '@/utils/format'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import ButtonConfirm from '@/components/ui/button-confirm' // use o mesmo confirm do seu projeto
import { useToast } from '@/lib/hooks/use-toast'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { useSignManyAgreements } from '../services/useSignManyAgreements copy'
import { useCurrentClient } from '@/features/auth/state/auth'
import { friendlyNameMaps } from '@/components/form/input/StatusMapper'
import { UploadContractButton } from './UploadContractButton'
import { generatePDF } from '../utils/pdf-util'
import { cleanHtmlContent } from '../utils/cleanHtmlContent'
import { getCleanDomain } from '@/utils/getCleanDomain'
import { getDomainIcon } from '../utils/domainIcon'
import { isNonEmptyHtml, isNonEmptyUrl } from '../utils/isNonEmptyHtml'

// hook fictício de assinatura em massa — renomeie para o seu real:

// 1) Acrescente esses campos na interface Agreement
export interface Agreement {
  id: string
  contracts?: string[] | null
  id_tenant?: string | null
  id_usuario?: string | null
  id_user?: string | null
  id_client?: string | null
  status?: 'Ativo' | 'Pendente' | 'Inativo' | string
  typeRegime?: string | null
  isFirst?: boolean | string | null
  isAssinado?: boolean | string | null
  IsAtivo?: boolean | string | null
  createdDate?: string | null
  htmlContent?: string | null
  pdf?: string | null // URL absoluta, relativa ou dataURL
}

interface AgreementTableProps {
  agreement: Agreement[]
  onClickAgreement: (agreement: Agreement) => void
}

export default memo(function AgreementTable({ agreement, onClickAgreement }: AgreementTableProps) {
  const [selected, setSelected] = useState<Agreement[]>([])
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const client = useCurrentClient()
  console.log(agreement)
  const { mutate: signMany, isPending: signing } = useSignManyAgreements()

  const handleCheckedChange = (checked: boolean, item: Agreement) => {
    setSelected(prev => {
      if (checked) {
        if (prev.find(p => p.id === item.id)) return prev
        return [...prev, item]
      }
      return prev.filter(p => p.id !== item.id)
    })
  }

  const handleSign = useCallback(() => {
    if (selected.length === 0) return
    const ids = selected.map(s => s.id)

    signMany(ids, {
      onSuccess: () => {
        // revalida as listas relacionadas
        queryClient.invalidateQueries({ queryKey: ['agreements'] })
        queryClient.invalidateQueries({ queryKey: ['agreement', 'minutas'] })
        setSelected([])
        toast({
          title: 'Contratos assinados!',
          description: `${ids.length} ${formatSingularOrPlural('contrato', 'contratos', ids.length)} foram assinados com sucesso.`
        })
      },
      onError: (error: unknown) => {
        const message =
          error instanceof AxiosError
            ? error.response?.data?.message || 'Falha ao assinar os contratos.'
            : 'Erro ao tentar assinar os contratos.'
        toast({ title: 'Erro', description: message, variant: 'destructive' })
      }
    })
  }, [selected, signMany, queryClient, toast])

  if (agreement.length === 0) {
    return <div className="text-center text-gray-500 py-10">Nenhuma minuta/contrato encontrado.</div>
  }

  const formatBool = (v: unknown) => {
    if (typeof v === 'boolean') return v ? 'Sim' : 'Não'
    if (typeof v === 'string') {
      const low = v.toLowerCase()
      if (['true', 'sim', '1'].includes(low)) return 'Sim'
      if (['false', 'não', 'nao', '0', 'naõ'].includes(low)) return 'Não'
    }
    return '-'
  }



  function sanitizeContract(s: string): string {
    // troca "_" por espaço, remove a palavra PRINCIPAL (case-insensitive),
    // remove espaços extras e aplica seu formatNome
    const cleaned = s
      .replace(/_/g, ' ')
      .replace(/\bprincipal\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim()

    return formatNome(cleaned)
  }

  const domain = getCleanDomain()
  const selectedIcon = getDomainIcon(domain)
  return (
    <>
      {/* Barra de ações quando há seleção */}
      <div
        className={`w-full py-2 px-1 transition-all duration-300 ${selected.length > 0 ? 'flex items-center gap-3' : 'hidden'}`}
      >
        <p className="text-xs text-primary">
          {selected.length} {formatSingularOrPlural('item', 'itens', selected.length)} selecionado
        </p>

        <ButtonConfirm
          confirmText={`Deseja assinar ${selected.length} ${formatSingularOrPlural('contrato', 'contratos', selected.length)} selecionado${selected.length > 1 ? 's' : ''}?`}
          onSuccess={handleSign}
          successBtnText={signing ? 'Assinando...' : 'Assinar selecionados'}
          disabled={signing}
          trigger={
            <Button size="sm" disabled={signing}>
              {signing ? 'Assinando...' : 'Assinar selecionados'}
            </Button>
          }
        />
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            {client && <TableHead className="w-[48px]">...</TableHead>}
            <TableHead className="w-[220px] md:w-[260px]">Produtos</TableHead>
            <TableHead className="hidden md:table-cell text-center">Status</TableHead>
            <TableHead className="text-center">Regime</TableHead>
            <TableHead className="hidden md:table-cell text-center">Assinado</TableHead>
            <TableHead className="hidden md:table-cell text-center">Em Uso</TableHead>
            <TableHead className="text-center">Criado Em</TableHead>

            {/* 👇 nova coluna */}
            <TableHead className="text-center">PDF</TableHead>

            <TableHead className="text-center">Anexar</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {agreement.map((item: Agreement, index: Key | null | undefined) => {
            const isRowChecked = !!selected.find(s => s.id === item.id)
            return (
              <TableRow
                key={item.id || String(index)}
                className="cursor-pointer hover:bg-muted transition"
                onClick={() => onClickAgreement(item)}
              >
                {client && (
                  <TableCell onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={isRowChecked}
                      onCheckedChange={v => handleCheckedChange(Boolean(v), item)}
                      aria-label="Selecionar linha"
                    />
                  </TableCell>
                )}
                <TableCell
                  className="font-medium max-w-[260px] truncate"
                  title={(() => {
                    const arr = Array.isArray(item.contracts) ? item.contracts : []
                    const map = friendlyNameMaps.typeServico

                    const friendly = arr
                      .filter((s): s is string => !!s && s.trim().length > 0)
                      .map(s => {
                        const raw = String(s)
                        const norm = raw
                          .replace(/\bPRINCIPAL\b/gi, '') // remove "PRINCIPAL"
                          .trim()
                        const key = norm.toUpperCase().replace(/\s+/g, '_') // espaços -> _
                        return map[key] ?? map[norm] ?? map[raw] ?? norm.replace(/_/g, ' ')
                      })
                      .filter((v, i, a) => a.indexOf(v) === i) // dedup

                    return friendly.length ? friendly.join(', ') : '-'
                  })()}
                >
                  {(() => {
                    const arr = Array.isArray(item.contracts) ? item.contracts : []
                    const map = friendlyNameMaps.typeServico

                    const friendly = arr
                      .filter((s): s is string => !!s && s.trim().length > 0)
                      .map(s => {
                        const raw = String(s)
                        const norm = raw.replace(/\bPRINCIPAL\b/gi, '').trim()
                        const key = norm.toUpperCase().replace(/\s+/g, '_')
                        return map[key] ?? map[norm] ?? map[raw] ?? norm.replace(/_/g, ' ')
                      })
                      .filter((v, i, a) => a.indexOf(v) === i)

                    if (friendly.length === 0) return '—'
                    const shown = friendly.slice(0, 3).join(' ')
                    const extra = friendly.length - 3
                    return extra > 0 ? `${shown}, +${extra}` : shown
                  })()}
                </TableCell>

                <TableCell className="hidden md:table-cell text-center">
                  {item.status === 'Ativo' && <Badge variant="outline">Ativo</Badge>}
                  {item.status === 'Pendente' && <Badge variant="outline">Pendente</Badge>}
                  {!['Ativo', 'Pendente'].includes(item.status || '') &&
                    (item.status ? <Badge variant="outline">{item.status}</Badge> : <span>-</span>)}
                </TableCell>

                <TableCell className="text-center">{sanitizeContract(item?.typeRegime ?? '') || '-'}</TableCell>

                <TableCell className="hidden md:table-cell text-center">{formatBool(item.isAssinado)}</TableCell>
                <TableCell className="hidden md:table-cell text-center">{formatBool(item.IsAtivo)}</TableCell>

                <TableCell className="text-center">{formatDate(item.createdDate)}</TableCell>

                <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                  {isNonEmptyHtml(item?.htmlContent) ? (
                    <Button
                      onClick={() =>
                        generatePDF(
                          cleanHtmlContent(item!.htmlContent as string),
                          selectedIcon,
                          item?.id_client ?? undefined
                        )
                      }
                    >
                      Baixar PDF
                    </Button>
                  ) : isNonEmptyUrl(item?.pdf) ? (
                    <Button variant="outline" asChild className=" bg-primary text-white hover:primary-hover">
                      <a href={item!.pdf as string} download>
                        Baixar PDF
                      </a>
                    </Button>
                  ) : (
                    <span>-</span>
                  )}
                </TableCell>

                {!item.isAssinado && (
                  <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                    <UploadContractButton idAgreement={item.id} />
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </>
  )
})
